import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { useUsageSummary } from '@/hooks/useUsageSummary';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

function centsToMoney(cents: number, currency: string, locale: string): string {
  return new Intl.NumberFormat(locale, { style: 'currency', currency, maximumFractionDigits: 2 }).format(cents / 100);
}

/** Cents as a short spoken figure: 10 → "10 cents", 600 → "$6.00". */
function centsToRate(cents: number, currency: string, locale: string): string {
  if (cents < 100) return `${cents} cent${cents === 1 ? '' : 's'}`;
  return centsToMoney(cents, currency, locale);
}

/** Settings → Call tracking usage: the meter, the rate card and the dollar alert. */
export function Usage() {
  const { summary, isLoading, setThreshold, isSavingThreshold } = useUsageSummary();
  const { locale, formatShortDate } = useCompanyLocale();
  const [thresholdInput, setThresholdInput] = useState('');

  useEffect(() => {
    if (summary) setThresholdInput((summary.threshold_cents / 100).toFixed(0));
  }, [summary]);

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> Loading usage…
      </div>
    );
  }
  if (!summary) {
    return <p className="text-sm text-muted-foreground">Usage is not available for this account.</p>;
  }

  const currency = summary.currency || locale.currency;
  const money = (cents: number) => centsToMoney(cents, currency, locale.locale);
  const rate = (cents: number | null) => (cents == null ? '—' : centsToRate(cents, currency, locale.locale));
  const threeMinuteCall = summary.call_minute_cents == null ? null : summary.call_minute_cents * 3;

  const handleSaveThreshold = async () => {
    const dollars = Number(thresholdInput);
    if (!Number.isFinite(dollars) || dollars < 0) {
      toast({ title: 'Enter a dollar amount', variant: 'destructive' });
      return;
    }
    try {
      await setThreshold(Math.round(dollars * 100));
      toast({ title: dollars === 0 ? 'Usage alert turned off' : `Alert set at ${money(Math.round(dollars * 100))} a month` });
    } catch (e) {
      toast({ title: 'Could not save the alert', description: e instanceof Error ? e.message : 'Unknown error', variant: 'destructive' });
    }
  };

  if (summary.ctm_mode !== 'agency') {
    return (
      <div className="space-y-6">
        <div>
          <h2 className="text-lg font-semibold">Call tracking usage</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your phone numbers run on your own CallTrackingMetrics account, so CallTrackingMetrics bills you
            directly and there is nothing to meter here.
          </p>
        </div>
        <RateCard summary={summary} rate={rate} threeMinuteCall={threeMinuteCall} money={money} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Call tracking usage</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          There is no call tracking subscription. You pay for the numbers you hold and the calls and texts you
          actually get, as one line on your monthly invoice.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">This month so far</CardTitle>
          <CardDescription>
            {formatShortDate(summary.period_start)} to {formatShortDate(summary.period_end)} · day {summary.days_elapsed} of {summary.days_in_month}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-5">
            <Stat label="Numbers" value={String(summary.numbers)} />
            <Stat label="Calls" value={String(summary.calls)} hint={`${Math.round(summary.call_minutes)} min`} />
            <Stat label="Texts" value={String(summary.texts)} />
            <Stat label="Spend so far" value={money(summary.total_cents)} emphasis />
            <Stat label="Projected month end" value={money(summary.projected_cents)} />
          </div>
          {summary.credit_cents > 0 && (
            <p className="mt-3 text-xs text-muted-foreground">
              You have {money(summary.credit_cents)} of usage credit. It comes off before anything is billed.
            </p>
          )}
        </CardContent>
      </Card>

      <RateCard summary={summary} rate={rate} threeMinuteCall={threeMinuteCall} money={money} />

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Monthly alert</CardTitle>
          <CardDescription>
            We email you and show a notice here when this month&apos;s usage passes this amount. Nothing stops;
            it is there so the invoice never surprises you. Set it to 0 to turn it off.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-end gap-3">
            <div className="space-y-1">
              <Label htmlFor="usage-threshold">Alert me when usage passes ({currency})</Label>
              <Input
                id="usage-threshold"
                type="number"
                min={0}
                step={5}
                inputMode="decimal"
                className="w-40"
                value={thresholdInput}
                onChange={(e) => setThresholdInput(e.target.value)}
              />
            </div>
            <Button onClick={handleSaveThreshold} disabled={isSavingThreshold}>
              {isSavingThreshold ? 'Saving…' : 'Save'}
            </Button>
          </div>
          {summary.alert_crossed && (
            <p className="mt-3 text-sm text-warning">
              This month&apos;s usage has already passed your alert ({money(summary.threshold_cents)}).
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function Stat({ label, value, hint, emphasis }: { label: string; value: string; hint?: string; emphasis?: boolean }) {
  return (
    <div className="rounded-md bg-muted/50 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={emphasis ? 'text-xl font-semibold' : 'text-lg font-semibold'}>{value}</div>
      {hint && <div className="text-xs text-muted-foreground">{hint}</div>}
    </div>
  );
}

function RateCard({
  summary,
  rate,
  threeMinuteCall,
  money,
}: {
  summary: { number_month_cents: number | null; call_minute_cents: number | null; text_message_cents: number | null };
  rate: (cents: number | null) => string;
  threeMinuteCall: number | null;
  money: (cents: number) => string;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">What you pay for</CardTitle>
        <CardDescription>
          Three things are metered. Recording, storage, the website number swap, the job webhook and attribution
          are all included in your plan.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="divide-y text-sm">
          <div className="flex items-center justify-between py-2">
            <dt>A tracking number, per month</dt>
            <dd className="font-medium">{rate(summary.number_month_cents)}</dd>
          </div>
          <div className="flex items-center justify-between py-2">
            <dt>A call minute, in or out</dt>
            <dd className="font-medium">{rate(summary.call_minute_cents)}</dd>
          </div>
          <div className="flex items-center justify-between py-2">
            <dt>A text message, sent or received</dt>
            <dd className="font-medium">{rate(summary.text_message_cents)}</dd>
          </div>
        </dl>
        {threeMinuteCall != null && (
          <p className="mt-3 text-xs text-muted-foreground">
            A typical three-minute call costs {money(threeMinuteCall)}. Missed calls and failed texts are free.
            Release a number and it stops billing that day. No minimums, no setup fee, no notice period.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export default Usage;
